import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import DeviceHub from "@mui/icons-material/DeviceHub";
import Language from "@mui/icons-material/Language";
import ArticleIcon from "@mui/icons-material/Article";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import { AccessTime, Person } from "@mui/icons-material";
import { Button, Tab, Tabs } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";

import { DEFAULT_MENU, HIDDEN_NAV_MENU_KEYS, SideNavBarMenu } from "./constants";
import { ACTION, usePermissions } from "../../../utils/permissions";
import { getAppEnvironmentName } from "../../../utilities/helpers/helperfunctions";
import WonderLogo from "../../../assets/imgs/wonder_logo_rebrand.svg";

interface AppBarProps extends MuiAppBarProps {
    scrolled?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "scrolled",
})<AppBarProps>(({ scrolled }) => ({
    background: scrolled ? "rgba(6, 6, 33, 0.9)" : "transparent",
    backdropFilter: scrolled ? "blur(10px)" : "none",
    boxShadow: scrolled ? "0 2px 10px rgba(0, 0, 0, 0.3)" : "none",
    transition: "background 0.3s ease, box-shadow 0.3s ease",
}));

const NavTab = styled(Tab)(({ theme }) => ({
    color: "rgba(255, 255, 255, 0.7)",
    minHeight: 64,
    textTransform: "none",
    fontSize: "0.95rem",
    fontWeight: 500,
    "&:hover": {
        color: "#fff",
        opacity: 1,
    },
    "&.Mui-selected": {
        color: "#fff",
        fontWeight: 600,
    },
}));

const getMenuIcon = (iconName: string) => {
    const iconProps = { sx: { mr: 1, fontSize: 20 } };
    switch (iconName) {
        case "DeviceHub":
            return <DeviceHub {...iconProps} />;
        case "Language":
            return <Language {...iconProps} />;
        case "Ingestion":
            return <CloudUploadIcon {...iconProps} />;
        case "Page":
            return <ArticleIcon {...iconProps} />;
        case "Metadata":
            return <ManageSearchOutlinedIcon {...iconProps} />;
        case "Avails":
            return <AccessTime {...iconProps} />;
        case "AvailsPlus":
            return <AccessTime {...iconProps} />;
        case "User":
            return <Person {...iconProps} />;
        case "Dsar":
            return <PrivacyTipOutlinedIcon {...iconProps} />;
        default:
            return null;
    }
};

export default function LandingPage(props: any) {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [activePath, setActivePath] = React.useState("");
    const [scrolled, setScrolled] = React.useState(false);

    const isNavMenuVisible = React.useCallback(
        (menu: { key?: string }) => {
            if (menu.key && HIDDEN_NAV_MENU_KEYS.includes(menu.key)) {
                return false;
            }

            if (!menu.key) {
                return true;
            }

            return (
                DEFAULT_MENU.includes(menu.key) ||
                hasPermission(menu.key, ACTION.VIEW)
            );
        },
        [hasPermission]
    );

    React.useEffect(() => {
        const visibleMenu = SideNavBarMenu.filter(isNavMenuVisible);

        if (visibleMenu.length > 0) {
            const firstPath = visibleMenu[0].path;
            if (window.location.pathname === "/") {
                navigate(firstPath);
                setActivePath(firstPath);
            } else {
                setActivePath(window.location.pathname);
            }
        }
    }, [isNavMenuVisible, navigate]);

    React.useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 50;
            setScrolled(isScrolled);
        };

        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    async function signOut() {
        try {
            await Auth.signOut();
        } catch (error) {
            console.log("error signing out: ", error);
        }
    }

    const filteredMenu = (menus: any[]) => menus.filter(isNavMenuVisible);

    const handleTabChange = (event: React.SyntheticEvent, newPath: string) => {
        setActivePath(newPath);
        navigate(newPath);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
            }}
        >
            <AppBar position="fixed" scrolled={scrolled}>
                <Toolbar sx={{ justifyContent: "space-between" }}>
                    {/* Left: Logo/Title */}
                    <img
                        src={WonderLogo}
                        alt="Canvas Logo"
                        style={{
                            height: "32px", // Matches Wonder header design spec for logo height
                            marginRight: "32px", // Provides consistent spacing between logo and navigation tabs
                            cursor: "pointer",
                        }}
                        onClick={() => navigate("/")} // Optional: click logo to go home
                    />

                    {/* Center: Navigation Tabs */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Tabs
                            value={activePath}
                            onChange={handleTabChange}
                            TabIndicatorProps={{
                                style: { backgroundColor: "#fff", height: 3 },
                            }}
                        >
                            {filteredMenu(SideNavBarMenu).map(
                                ({ path, label, menuIcon }) => (
                                    <NavTab
                                        key={path}
                                        label={
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {getMenuIcon(menuIcon)}
                                                {label}
                                            </Box>
                                        }
                                        value={path}
                                    />
                                )
                            )}
                        </Tabs>
                    </Box>

                    {/* Right: Logout Button */}
                    <Button
                        onClick={signOut}
                        variant="contained"
                        color="primary"
                        startIcon={<LogoutIcon />}
                    >
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    mt: 8, // Offset for fixed AppBar
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                {props.children}
            </Box>
        </Box>
    );
}
